/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import { Rating } from '@hyphengroup/cms-components/components/Rating';
import { useState } from 'react';
import Button from '../../Button';
import ArrowRightAlt from '../../icons/ArrowRightAlt';
import AppTabs from '../components-internal/AppTabs';
import { Container } from '../components-internal/Container';
import { slugify } from '../helpers';
import { CONTENT_MHG } from '../translations/mhg';
import { ComponentReviewCards } from '$/(cms)/_modules/cmsService/types';
import { useClientContext } from '$/modules/context/client';

export interface ReviewCardsProps {
  title: string;
  tabs: Array<{
    name: string;
    items: ReviewItem[];
  }>;
}

interface ReviewItem {
  image: string;
  title: string;
  rating_score?: number;
  rating_text?: string;
  rating_additional_information?: string;
  description: string;
  link?: string;
}

export const ReviewCards: React.FC<ComponentReviewCards> = (data) => {
  const tabs =
    data?.tabs?.map((e) => ({
      label: e?.name ?? '',
      key: slugify(e?.name ?? ''),
      items: e?.items?.filter((i) => !!i) ?? [],
    })) ?? [];

  const { lang } = useClientContext();
  const staticText = CONTENT_MHG.get(lang);
  const [activeTabKey, setActiveTabKey] = useState(tabs[0]?.key);

  const handleLinkClick = (url?: string) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div>
      <Container className="mx-auto max-w-screen-2xl gap-[--spacing-gap-section-y-lg]">
        <h3 className="mt-0 py-[--spacing-padding-y-xs] pt-0 alpha-display-1">
          {data.title}
        </h3>
        <div className="rounded-2xl p-4 shadow bg-white md:rounded-3xl md:px-6">
          <AppTabs
            tabs={tabs}
            type="underline"
            activeTabKey={activeTabKey}
            onTabChange={setActiveTabKey}
          >
            {tabs
              .find((t) => t.key === activeTabKey)
              ?.items.map((item) => (
                <Container
                  key={item.title}
                  xType="padding-x"
                  yType="padding-y"
                  className="min-h-35 border-b border-gray-300 !px-0"
                >
                  <div className="flex h-full flex-col md:flex-row">
                    <div className="mb-4 flex w-full items-start md:w-1/4">
                      {item.image?.[0]?.url && (
                        <CmsImage
                          unoptimized
                          src={item.image[0]?.url}
                          alt="Review"
                          className="inline h-[165px] object-contain pr-8 md:w-full"
                          width={280}
                          height={190}
                        />
                      )}
                    </div>

                    <div className="flex w-full grow flex-col md:flex-row">
                      <div className="flex grow flex-col">
                        <h3 className="mb-4 alpha-title-2">{item.title}</h3>

                        {item.rating_score && (
                          <Rating
                            score={item.rating_score}
                            info={
                              item.rating_additional_information ?? undefined
                            }
                            className="mb-5"
                          />
                        )}
                        {item.description && (
                          <Container>
                            <h2 className="alpha-body-2">
                              <span
                                className="font-medium"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                  __html: item.description,
                                }}
                              />
                            </h2>
                          </Container>
                        )}
                      </div>

                      <div className="ml-0 mt-4 flex items-end md:ml-auto md:justify-end">
                        {item.link && (
                          <Button
                            variant="text"
                            onClick={() =>
                              handleLinkClick(item.link ?? undefined)
                            }
                            color="text-primary"
                            className="mb-0 ml-3 p-0 md:ml-0"
                            endIcon={<ArrowRightAlt />}
                          >
                            {staticText.more_details}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </Container>
              ))}
          </AppTabs>
        </div>
      </Container>
    </div>
  );
};
